(ns app.front.view.group.admin.complaint-config
  (:require [app.domain.complaint.model :as complaint-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.tag-type-editor :as ui-tag-type-editor]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.core :as core]
            [app.front.view.group.admin.feature-toggle :as feature-toggle]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::data
  :-> ::data)

(rf/reg-sub ::configs
  :<- [::gas/root-group-configs]
  (fn [root-configs _]
    (some->> root-configs
             (filter (comp ptc-model/permit-types-with-complaints :group.config/target))
             seq
             du/entities->entity-db)))

(rf/reg-sub ::config-ids
  :<- [::configs]
  :-> keys)

(rf/reg-sub ::config-labels
  :<- [::configs]
  :<- [::gas/group-tr]
  (fn [[configs tr] _]
    (reduce-kv
      (fn [m config-id config]
        (assoc m config-id (tr [(:group.config/target config)])))
      {}
      configs)))

(rf/reg-event-fx ::set-config-id
  (fn [{:keys [db]} [_ config-id]]
    {:pre [(int? config-id)]}
    {:db (assoc db ::data {:config-id config-id})}))

(rf/reg-sub ::config-id
  :<- [::data]
  :<- [::config-ids]
  (fn [[data ids] _]
    (or (:config-id data)
        (first ids))))

(rf/reg-sub ::config
  :<- [::configs]
  :<- [::config-id]
  (fn [[configs id] _]
    (get configs id)))

(rf/reg-sub ::group-id
  :<- [::config]
  (fn [config _]
    (-> config :group.config/group :db/id)))

(rf/reg-sub ::section-available?
  :<- [::configs]
  :-> some?)

(defmulti handle-config-entry
  "Register a handler for a particular `:group.config/*` key.
  The handler may postprocess the corresponding value if needed."
  (fn [key _value]
    {:pre [(keyword? key)]}
    key))

(rf/reg-sub ::original-values
  :<- [::config]
  (fn [config _]
    (let [known-keys (-> handle-config-entry methods keys)
          config     (select-keys config known-keys)]
      (reduce
        (fn [m k]
          (update m k (partial handle-config-entry k)))
        config
        known-keys))))

(rf/reg-sub ::modified-values
  :<- [::data]
  :-> :modified-values)

(rf/reg-sub ::data-to-store
  :<- [::current-values]
  :<- [::modified-values]
  (fn [[current modified]]
    (let [modified-keys (keys modified)]
      (select-keys current modified-keys))))

(rf/reg-sub ::current-values
  :<- [::original-values]
  :<- [::modified-values]
  (fn [[original modified] _]
    (p/deep-merge original (or modified {}))))

(rf/reg-sub ::current-value
  :<- [::current-values]
  (fn [values [_ attr]]
    (get values attr)))

(rf/reg-sub ::unchanged?
  :<- [::original-values]
  :<- [::current-values]
  (fn [[original current] _]
    (= current original)))

(rf/reg-event-fx ::cleanup
  (fn [{:keys [db]} _]
    {:db (dissoc db ::data)}))

(rf/reg-event-fx ::set-attr
  (fn [{:keys [db]} [_ attr value]]
    {:pre [(keyword? attr) (some? value)]}
    {:db (assoc-in db [::data :modified-values attr] value)}))

(rf/reg-event-fx ::save-group-config.success
  (fn [{:keys [db]} _]
    {:db (update db ::data dissoc :modified-values)}))

(rf/reg-event-fx ::set-in-attr
  (fn [{:keys [db]} [_ & more]]
    {:db (assoc-in db (concat [::data :modified-values] (butlast more)) (last more))}))

(rf/reg-event-db ::set-open-sev
  (fn [db [_ sev]]
    (assoc db ::open-severity sev)))

(rf/reg-sub ::open-severity
  :-> ::open-severity)

;; Complaint photo settings

(defn- complaint-photo-settings [tr]
  (let [photos-enabled? (<sub [::gas/has-feature? :group.feature/complaint-photos])
        group-id        (<sub [::group-id])]
    (when (<sub [::ff/enabled? :feature-flag.id/complaint-photos])
      [:div
       [:div
        [:h4 (tr [:group-admin/enable-photos] [complaint-model/allowed-amount-of-images])]
        [:p (tr [:group-admin/complaint-photos-instruction])]
        [:div.flex.flex-wrap.w-full
         (cc/toggle {:toggled   photos-enabled?
                     :id        "complaint-photos-toggle"
                     :labelA    (tr [:no])
                     :labelB    (tr [:yes])
                     :on-toggle (fn [checked]
                                  (>evt [::feature-toggle/toggle-group-feature
                                         group-id
                                         :group.feature/complaint-photos
                                         checked]))})]]])))

(defn- toggle [tr severity label default]
  [:div.mt-2.mr-10
   (let [config-key       :group.config/complaint-severity-configs
         config-attribute :domain/state
         value            (if-some [value (get-in (<sub [::current-value config-key]) [severity config-attribute])]
                            (= :complaint-severity-config.state/enabled value)
                            default)]
     (cc/toggle {:toggled    value
                 :id         (str severity)
                 :labelA     (tr [:no])
                 :labelB     (tr [:yes])
                 :label-text (tr [label])
                 :on-toggle  #(>evt [::set-in-attr config-key severity config-attribute
                                     (if value
                                       :complaint-severity-config.state/disabled
                                       :complaint-severity-config.state/enabled)])}))])

(defn- text-editor [{:keys [tr open? editor-lang group-config-key config-key config-attribute initial-text placeholder]}]
  (let [op-key (or config-key group-config-key)]
    [:div.p-3.border.border-grey.border-solid.mt-2.w-full
     {:data-cy "permit-type-row"
      :class   (if open? "bg-grey-tint-7" "bg-white")}
     [:div.flex.justify-between.items-start.cursor-pointer
      {:on-click #(>evt [::set-open-sev (when-not open? op-key)])}
      [:h4 (tr [op-key])]
      (cc/icon
        {:icon        (if open? i/chevron-up i/chevron-down)
         :description ""
         :class-name  "mt-2"})]
     (when open?
       [:div.flex.flex-wrap.w-full
        [ui-i18n/edit-language-selector {:allowed-locales-sub ::ui-i18n/languages}]])
     [ui-rt/editor-legacy
      {:id            (str editor-lang op-key)
       :initial-value (or (get-in (<sub [::current-value group-config-key])
                                  (remove nil? [config-key config-attribute editor-lang]))
                          initial-text)
       :placeholder   placeholder
       :on-change     (when open? #(>evt (filterv some? [::set-in-attr group-config-key config-key config-attribute editor-lang %])))}]]))

(def severity->help-text-key
  {:complaint-severity/low       :complaint-severity-help-text/low
   :complaint-severity/standard  :complaint-severity-help-text/standard
   :complaint-severity/high      :complaint-severity-help-text/high
   :complaint-severity/emergency :complaint-severity-help-text/emergency})

(defn- help-text-editor [tr editor-lang severity open-severity-editor]
  [text-editor (util/mmap {:group-config-key :group.config/complaint-severity-configs
                           :initial-text     (tr [(severity severity->help-text-key)])
                           :config-key       severity
                           :config-attribute :complaint-severity-config/help-texts
                           :open?            (= open-severity-editor severity)}
                          tr editor-lang)])

(defn- severity-settings [tr]
  (when (ff/enabled? :feature-flag.id/complaints-priority)
    (let [visibility-key :group.config/complaint-severity-use
          visibility     (or (<sub [::current-value visibility-key])
                             config-model/default-complaint-severity-use)
          editor-lang    (<sub [::ui-i18n/editor-language ::ui-i18n/languages])]
      [:div
       [:h4.mb-6 (tr [:complaint-severity/title])]
       [:div.flex.flex-wrap.w-full
        [ui-radio/radio {:name      (name visibility-key)
                         :values    config-model/complaint-severity-use
                         :value     visibility
                         :on-change #(>evt [::set-attr visibility-key %])
                         :val->str  #(tr [%])}]]
       (for-frag [severity config-model/complaint-severity]
         [help-text-editor tr editor-lang severity (<sub [::open-severity])])
       [:div.flex.flex-wrap.w-full
        [toggle tr :complaint-severity/low :complaint-severity/show-low-severity true]
        [toggle tr :complaint-severity/emergency :complaint-severity/show-emergency-severity false]]])))


;; Severity visibility settings

(defmethod handle-config-entry :group.config/complaint-severity-configs
  [_ values]
  (reduce
    (fn [acc m]
      (assoc acc
             (:complaint-severity-config/severity m)
             {:domain/state                         (:domain/state m)
              :complaint-severity-config/help-texts (i18n-model/strings-map (:complaint-severity-config/help-texts m))}))
    {} values))

(defmethod handle-config-entry :group.config/complaint-severity-use
  [_ value]
  value)

(defmethod handle-config-entry :group.config/complaint-sign-off-disclaimers
  [_ value]
  (i18n-model/strings-map value))

(defmethod handle-config-entry :group.config/complaint-sign-off-required?
  [_ value]
  (boolean value))

(defmethod handle-config-entry :group.config/public-map-hide-complaints
  [_ value]
  (boolean value))

(defn- public-map-settings
  [tr]
  (let [has-public-map?      (<sub [::gas/has-feature? :group.feature/public-map])
        hide-complaints-attr :group.config/public-map-hide-complaints
        hide-complaints?     (<sub [::current-value hide-complaints-attr])]
    (when has-public-map?
      [:div
       [:h4 (tr [:group-admin/public-view-hide-complaints])]
       [:div.flex.flex-wrap.w-full
        (cc/toggle {:toggled   hide-complaints?
                    :id        (name hide-complaints-attr)
                    :labelA    (tr [:no])
                    :labelB    (tr [:yes])
                    :on-toggle #(>evt [::set-attr hide-complaints-attr (not hide-complaints?)])})]])))


;;; Required contacts

(defmethod handle-config-entry :group.config/ce-complaint-required-contact-information
  [_ values]
  (set values))

(defn- required-contacts
  [tr]
  (let [attr   :group.config/ce-complaint-required-contact-information
        values (<sub [::current-value attr])]
    [:div
     [:h4 (tr [:group-admin/required-contact-information])]
     [:p (tr [:group-admin/complaints-contacts-instruction])]
     [:div.flex.flex-wrap.w-full
      (for-frag [field complaint-model/complaint-contact-fields]
        [:div.mt-2.mr-4.last:mr-0
         (cc/toggle {:toggled    (contains? values field)
                     :id         (name field)
                     :labelA     (tr [:no])
                     :labelB     (tr [:yes])
                     :label-text (tr [field])
                     :on-toggle  #(let [op (fnil (if % conj disj) #{})]
                                    (>evt [::set-attr attr (op values field)]))})])]]))

;;; Complaint sources

(defmethod handle-config-entry :group.config/available-complaint-sources
  [_ entities]
  (->> entities
       (map :db/id)
       set))

(rf/reg-event-fx
  ::fetch-complaint-sources
  (fn [_ [_ group-id]]
    (let [cmd :group/complaint-sources]
      {:fx [[:dispatch [::core/set-fetching-config group-id cmd]]
            [:comm/command {:command    cmd
                            :method     :get
                            :data       {:group-id group-id}
                            :on-success [::complaint-sources-fetched group-id cmd]}]]})))

(rf/reg-event-fx
  ::complaint-sources-fetched
  (fn [{:keys [db]} [_ group-id command status {:keys [entities] :as resp}]]
    {:db (-> db
             (assoc-in [::data :sources-in-use] (:sources-in-use resp)))
     :fx [[:dispatch [::core/config-fetched group-id command status entities]]]}))


(rf/reg-sub ::complaint-sources-in-use
  :<- [::data]
  (fn [data _]
    (get data :sources-in-use #{})))

(rf/reg-event-fx ::create-new-complaint-source
  (fn [{:keys [db]} [_ group-id {:keys [names on-done]}]]
    {:pre [(int? group-id) (map? names) (fn? on-done)]}
    {:db           (assoc-in db [::data :creating-complaint-source?] true)
     :comm/command {:command    :group/add-complaint-source
                    :method     :post
                    :data       (util/mmap group-id names)
                    :on-success [::create-new-complaint-source.success on-done]
                    :on-done    [::create-new-complaint-source.done]}}))

(rf/reg-event-fx ::create-new-complaint-source.success
  (fn [{:keys [db]} [_ on-done status response]]
    (on-done)
    {:db         (update db ::data dissoc :creating-complaint-source?)
     :dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :complaint-source-editor/add-success]]}))

(rf/reg-event-fx ::create-new-complaint-source.done
  (fn [{:keys [db]} [_ status body request-id]]
    {:db       (update db ::data dissoc :creating-complaint-source?)
     :dispatch (when (>= status 400)
                 (comm/error-notification-evt status body request-id))}))

(rf/reg-sub ::creating-complaint-source?
  :<- [::data]
  (fn [data _]
    (-> data :creating-complaint-source? boolean)))

(rf/reg-event-fx ::remove-complaint-source
  (fn [_ [_ id]]
    {:pre [(int? id)]}
    {:comm/command {:command    :group/remove-complaint-source
                    :method     :post
                    :data       {:id id}
                    :on-success [::remove-complaint-source.success]}}))

(rf/reg-event-fx ::remove-complaint-source.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :complaint-source-editor/remove-success]]}))

(rf/reg-event-fx ::rename-complaint-source
  (fn [_ [_ language {:keys [id] :as item}]]
    {:pre [(keyword? language)]}
    (let [name (get-in item [:name language])]
      {:comm/command {:command    :group/rename-complaint-source
                      :method     :post
                      :data       (util/mmap id name language)
                      :on-success [::rename-complaint-source.success]}})))

(rf/reg-event-fx ::rename-complaint-source.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/merge-entity-db status response]
                  [:notifications/add :toast :success :success :complaint-source-editor/update-success]]}))

(rf/reg-sub ::complaint-sources
  :<- [::group-id]
  (fn [group-id]
    (<sub [::entities/entities {:entity/type            :entity.type/complaint-source
                                :domain/authority-group group-id}])))

(defn- complaint-source->item
  [lang entity]
  (let [label (-> entity (complaint-model/complaint-source-label lang))]
    {:id       (:db/id entity)
     :label    label
     :alphabet (-> label str/upper-case first)
     :name     (-> entity :complaint-source/names i18n-model/strings-map)}))

(rf/reg-sub ::complaint-source-items
  :<- [::complaint-sources]
  :<- [:language]
  (fn [[sources lang]]
    (->> sources
         (map (partial complaint-source->item lang))
         (sort-by :label))))

(defn- available-complaint-sources-editor
  [tr]
  (let [attr     :group.config/available-complaint-sources
        values   (<sub [::current-value attr])
        choices  (<sub [::complaint-source-items])
        group-id (<sub [::group-id])
        language (<sub [::ui-i18n/editor-language ::gas/allowed-locales])]
    [:div
     [:h4 (tr [:group-admin/complaint-sources-title])]
     [ui-tag-type-editor/editor
      (util/mmap {:values              choices
                  :selected?           (fn [{:keys [id]}]
                                         (contains? values id))
                  :on-toggle           (fn [checked? {:keys [id]}]
                                         (let [op (fnil (if checked? conj disj) #{})]
                                           (>evt [::set-attr attr (op values id)])))
                  :on-end-edit         (fn [item]
                                         (let [language (ui-i18n/get-actual-language-to-edit (:name item) language)]
                                           (>evt [::rename-complaint-source language item])))
                  :edit-disabled?      (comp (<sub [::complaint-sources-in-use]) :id)
                  :group-by-fn         (when (> (count choices) 5) :alphabet)
                  :name-attr           :name
                  :on-create           #(>evt [::create-new-complaint-source group-id %])
                  :on-delete           #(>evt [::remove-complaint-source (:id %)])
                  :create-in-progress? (<sub [::creating-complaint-source?])
                  :tag-type-name       (tr [:complaint-source-editor/complaint-source])
                  :remove-confirmation (tr [:complaint-source-editor/remove-confirmation])}
                 tr)]]))

(defn- disclaimer-text-editor [tr editor-lang open?]
  [text-editor (util/mmap {:group-config-key :group.config/complaint-sign-off-disclaimers
                           :placeholder      :complaint-sign-off/no-disclaimer}
                          open? tr editor-lang)])

(defn- sign-off-settings [tr]
  (when (ff/enabled? :feature-flag.id/complaint-sign-off-may-be-required)
    (let [group-config-key :group.config/complaint-sign-off-required?
          required?        (boolean (<sub [::current-value group-config-key]))
          editor-lang      (<sub [::ui-i18n/editor-language ::ui-i18n/languages])]
      [:div
       [:h4.mb-6 (tr [:complaint-sign-off/title])]
       [:div.flex.flex-wrap.w-full
        (cc/toggle {:toggled    required?
                    :id         (str group-config-key)
                    :labelA     (tr [:no])
                    :labelB     (tr [:yes])
                    :label-text (tr [group-config-key])
                    :on-toggle  #(>evt [::set-in-attr group-config-key (not required?)])})]
       [:div.flex.flex-wrap.w-full
        [disclaimer-text-editor tr editor-lang required?]]])))

;;; Actions

(defn- action-buttons
  [tr]
  (let [unchanged? (<sub [::unchanged?])
        group-id   (<sub [::group-id])
        config-id  (<sub [::config-id])
        values     (<sub [::data-to-store])]
    [ui-action-buttons/save-cancel-buttons
     (util/mmap {:on-cancel        #(>evt [::cleanup])
                 :on-save          #(>evt [::core/save-group-config
                                           group-id
                                           config-id
                                           values
                                           (constantly [::save-group-config.success])])
                 :save-disabled?   unchanged?
                 :cancel-disabled? unchanged?
                 :size             :small}
                tr)]))

;;; Configuration selector

(defn configuration-selector
  []
  (let [labels (<sub [::config-labels])]
    [ui-cs/content-switcher
     {:items     (<sub [::config-ids])
      :val->str  (fn [id] (get labels id))
      :current   (<sub [::config-id])
      :on-change (fn [id] (>evt [::set-config-id id]))
      :class     "mb-4"}]))

;;; Section

(defn complaint-config-section
  [& [section-options]]
  (when (<sub [::section-available?])
    (let [tr (<sub [::gas/group-tr])]
      [acommon/config-section
       (tr [:group-admin/complaints])
       (tr [:group-admin/complaints-instruction])
       section-options
       [:<>
        [configuration-selector tr]
        [acommon/admin-config-form
         [:div.flex.flex-col.gap-y-6
          [severity-settings tr]
          [complaint-photo-settings tr]
          [required-contacts tr]
          [available-complaint-sources-editor tr]
          [public-map-settings tr]
          [sign-off-settings tr]
          [action-buttons tr]]]]])))
